/* **The screen header of KANON klienci v2** — the title, its controls, and the statistics under
 * them. Transcribed from `Klienci - lista v2.dc.html`; promoted out of
 * `pages/client-roster/ui/RosterHeader.module.css` on 3 September 2026 on its second consumer.
 *
 * The second consumer is the plan library. KANON `Programy v2 - D3 (0.8)` is a HYBRID: everything
 * from its panes down is drawn at four fifths, and its page header is the roster's at 1:1 — the
 * same 28/30/24 measure inset, the same 31px title, the same 36px square flush right on the title's
 * row, the same three figures at 22px over 10px labels, the same 14/30 gaps. That is the owner's
 * 11 August disposition arriving on the screen it was written for: "nagłówki mają się kończyć w tym
 * samym miejscu", pinned by `e2e/header-parity.spec.ts`.
 *
 * Two screens declaring these values in two stylesheets is the equality nobody watches. One file is.
 *
 * ── What v2 changed, kept here because it explains the numbers ───────────────────────────────
 * v1 drew the header, the toolbar and the table as one uninterrupted `--bg-card` surface running
 * edge to edge. v2 puts the title and the statistics on the canvas and gives the content its own
 * bordered, rounded card underneath. So there is no `background` here and no page padding —
 * `shared/ui/canonScreen.module.css` owns the measure and its 28/30/24 inset, and this block sits
 * at the top of it with a 14px gap between its two rows.
 *
 * Every value below is read off `Klienci - lista v2.dc.html`:
 *   `head`      flex column, gap 14
 *   `h1`        31px / 800 / -.036em / 1.05
 *   `ghost`     36x36, radius 12, `--bg-card`, hairline `--border-2`, `--shadow-card`
 *   `ghostBadge` 15px tall, min 15 wide, ink fill, canvas ink, 9.5px/700, 2px canvas ring
 *   `primary`   36x36, radius 12, the ember gradient
 *   `kpiStack`  flex, align-items flex-end, gap 30
 *   `num`       22px / 800 / -.03em / 1
 *   `lab`       10px / 700 / .13em
 *
 * `.ghost` and `.ghostBadge` are the ROSTER's controls and the library draws neither. They stay
 * here rather than moving back: the archive square is the canon's second header control, the next
 * screen that needs one takes it from here, and a class nobody applies costs a build nothing.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.header {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-14px);
}

/* `align-items: center` is v2's own, against v1's `flex-end`: the two controls are now 36px squares
 * rather than 34px worded buttons, and the canon centres them on the title's line box. The `flex: 1`
 * spacer between the word and the pair is `justify-content: space-between` here — the same outcome
 * with one node fewer, which is the move `DetailTab.module.css` already makes for its own head. */
.titleRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

/* `flex: 0 1 auto`, not `flex: 1`, with `margin-inline-end: auto` doing the pushing instead.
 * The `h1` takes focus on a route change, and a grown flex item drew the ring as a rectangle around
 * "Klienci" and the empty space after it — which on a cold load is the first thing on screen. It
 * still shrinks, so a longer title has somewhere to go. */
.title {
  flex: 0 1 auto;
  min-width: 0;
  margin: var(--space-0) auto var(--space-0) var(--space-0);
  font-size: var(--fs-roster-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-screen-title);
  color: var(--text);
}

/* The `h1` takes focus on a route change so the shell can announce the screen; it is not a control,
 * so the ring is the only thing that should happen. */
.title:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--outline-inset);
}

/* ── The archive button, and the badge that hangs off it ─────────────────────────────────────── */

.ghost {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-card);
  cursor: pointer;
  transition-duration: var(--dur-fast);
  transition-property: color, background;
  transition-timing-function: var(--ease);
}

.ghost:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.ghost:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.ghostIcon {
  display: block;
  width: 18px;
  height: 18px;
}

/* The canon's badge: INK on the canvas, not the accent. It counts people waiting on an answer, and
 * screen 09 spends its one ember on the invite button. The 2px ring is `--bg`, the colour behind
 * the header — the badge overhangs the button's corner and the ring is what keeps its silhouette
 * legible over the border it crosses. */
.ghostBadge {
  position: absolute;
  top: -3px;
  right: -3px;
  display: grid;
  place-items: center;
  min-width: 15px;
  height: 15px;
  padding: var(--space-0) var(--space-1);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--bg);
  background: var(--text);
  border: var(--border-focus) solid var(--bg);
  border-radius: var(--r-pill);
  font-variant-numeric: tabular-nums;
}

/* ── The invite button: the screen's ONE accent ──────────────────────────────────────────────── */

/* ── Two of the canon's four shadows are drawn, and the split is the hard ban rather than taste ──
 * v2 gives this button four: `inset 0 1px 0 rgba(255,255,255,.30)`, `inset 0 -1px 0 rgba(0,0,0,.14)`,
 * `0 1px 2px rgba(196,58,10,.26)` and `0 5px 12px -4px rgba(196,58,10,.30)`.
 *
 * The two OUTER ones are blurs in the accent's own hue. `CLAUDE.md`: "no coloured glow, anywhere,
 * ever… not on buttons… no box-shadow, text-shadow or filter whose colour is anything but neutral
 * ink". The override outranks the handoff by its own terms, so they are not drawn — here or on any
 * other primary in the app.
 *
 * The two INSET ones are pure white and pure black, the ban does not reach them, and they are what
 * gives the fill its top lip and bottom edge. They live on `--edge-cta-lip-top` and
 * `--edge-cta-lip-bottom`, and `shared/ui/emberButton.module.css` draws them — which is where every
 * primary in the app takes them from. An earlier revision of this comment dropped them along with
 * the glow, on the argument that they existed only to give the coloured lift something to sit
 * under; the owner compared the two buttons side by side on 1 September 2026 and the flat one read
 * as flat.
 *
 * `.ember` carries the fill, the ink, the border, the lip, the hover and the focus ring. Nothing
 * here may restate any of them — see that file's header for what happened the last time a surface
 * did. */
.primary {
  width: 36px;
  height: 36px;
  padding: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  letter-spacing: normal;
  border-radius: var(--radius-12px);
}

.primaryIcon {
  display: block;
  width: 19px;
  height: 19px;
}

/* ── The statistics stack ────────────────────────────────────────────────────────────────────── */

/* `margin: 0` because this is a `<dl>`, and `base.css` resets the headings, `p`, `figure` and the
 * two list types — but not the definition list, whose UA `margin-block: 1em` was adding 14px above
 * and below and pushing the whole header 28px past the canon. */
.stats {
  display: flex;
  gap: var(--space-30px);
  align-items: flex-end;
  margin: var(--space-0);
}

.stat {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
}

/* `order: -1` puts the number above its word, which is the canon's arrangement. The DOM keeps `dt`
 * before `dd`, because a definition list's grammar requires it and because "aktywnych, 7" is the
 * reading a listener can follow. `dd` also carries a user-agent `margin-inline-start` that has to
 * go, or every figure sits 40px right of its label. */
.figure {
  order: -1;
  margin: var(--space-0);
  font-size: var(--fs-roster-kpi);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-display);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* ── The canon's `lab`, whole ────────────────────────────────────────────────────────────────
 * 10px / 700 / .13em in `--text-3`. The register came back on the owner's 31.08 decision and the
 * INK came back on 1 September 2026, with the rest of the repository's standing contrast
 * deviation: `--text-3` measures 2.17–2.80 against these surfaces, which is why it was raised to
 * `--text-2`, and the owner's answer is that the colours are the handoff's unless he says
 * otherwise. AKTYWNYCH / ZAPROSZONYCH / ŁĄCZNIE are still the only thing telling three bare
 * numbers apart, so the measurement is in the round's report rather than acted on here. */
.label {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  color: var(--text-3);
  white-space: nowrap;
}

